<template>
  <div class="slideshow-section container">
    <div class="slideshow-section__player" v-if="playable">
      <div
        class="slideshow-section__stage"
        ref="stage"
        :class="{
          'is-counting': auto === 'counting',
          'is-paused': started && !playing,
          'is-peeking': peeking,
        }"
        :style="{ '--slideshow-wait': `${AUTO_DELAY_MS}ms` }"
        @pointerdown="peek"
        @pointerdown.capture="onTouch"
      >
        <canvas
          aria-hidden="true"
          class="slideshow-section__canvas"
          ref="canvas"
        />
        <p aria-live="polite" class="screen-reader-text">
          {{ started ? caption : "" }}
        </p>

        <Transition name="slideshow-section-fade">
          <div class="slideshow-section__poster" v-if="!started">
            <img
              alt=""
              class="slideshow-section__poster-image"
              decoding="async"
              loading="lazy"
              v-if="poster"
              :src="poster"
            >
            <!-- A click anywhere on the picture starts the film, as on the hero. -->
            <button
              class="slideshow-section__poster-open"
              tabindex="-1"
              type="button"
              :aria-label="texts.play"
              @click="play()"
            />
            <div class="slideshow-section__poster-text">
              <small>{{ inMemoryOf }}</small>
              <b>{{ name }}</b>
              <span v-if="years">{{ years }}</span>
            </div>
            <!-- While it waits to play by itself, the pill fills from the left. -->
            <button
              class="slideshow-section__play"
              type="button"
              :aria-label="texts.play"
              @click="play()"
            >
              <i aria-hidden="true" class="slideshow-section__play-fill" />
              <span class="slideshow-section__play-label">
                <SlideshowIcon name="play" />
                {{ texts.heading }}
              </span>
            </button>
          </div>
        </Transition>

        <!-- Also while it plays by itself: pause, the bar and the larger player as ever. -->
        <SlideshowControls
          class="slideshow-section__controls"
          expandable
          v-if="started"
          :duration="duration"
          :playing="playing"
          :time="time"
          @expand="expand"
          @seek="seek"
          @toggle="toggle"
        />
      </div>

      <ol class="slideshow-section__strip" ref="stripEl">
        <li v-for="item in strip" :key="item.id">
          <button
            class="slideshow-section__thumb"
            type="button"
            :aria-current="item.id === currentId ? 'true' : undefined"
            :aria-label="sprintf(texts.playFrom, item.number)"
            :class="{ 'is-current': item.id === currentId, 'is-film': item.film }"
            @click="play(item.id)"
          >
            <img
              alt=""
              decoding="async"
              height="96"
              loading="lazy"
              width="96"
              :src="item.thumb"
            >
            <i
              aria-hidden="true"
              class="slideshow-section__thumb-progress"
              v-if="started && item.id === currentId"
              :style="{ transform: `scaleX(${slideProgress})` }"
            />
          </button>
        </li>
      </ol>
    </div>

    <div class="slideshow-section__side">
      <small class="slideshow-section__eyebrow">{{ texts.heading }}</small>
      <h2 class="slideshow-section__name">{{ name }}</h2>
      <p v-if="playable">{{ texts.sectionLead }}</p>
      <template v-else>
        <p>{{ askForMedia }} {{ texts.descNoSlideshow }}</p>
        <!-- Three places, the page's photos first: no count in words. -->
        <ul class="slideshow-section__photos">
          <li v-for="item in pagePhotos" :key="item.id">
            <img
              alt=""
              decoding="async"
              height="160"
              loading="lazy"
              width="160"
              :src="item.thumb"
            >
          </li>
          <li v-for="place in emptyPlaces" :key="place.title">
            <button
              class="slideshow-section__photos-add"
              type="button"
              :aria-label="`${addPhotoText}: ${place.title}`"
              @click.prevent="addPhoto"
            >
              <svg aria-hidden="true" class="icon">
                <use href="#plus" />
              </svg>
              <strong>{{ place.title }}</strong>
              <small>{{ place.hint }}</small>
            </button>
          </li>
        </ul>
      </template>
      <button
        class="btn obituary slideshow-section__add"
        type="button"
        @click.prevent="addPhoto"
        v-text="addPhotoText"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    shallowRef,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import {
    CONTROLS_IDLE_MS,
    useCaptionLift,
  } from "../../composables/useControls";
  import {
    assets,
    firstFrame,
    hasSlideshow,
    loadPageContent,
    openViewer,
    PHONE_QUERY,
    quickContent,
    slideshowData,
    slideshowTexts,
    useMediaQuery,
    usePrefersReducedMotion,
  } from "../../composables/useSlideshow";
  import { MEDIA_INITIAL } from "../../createCommentStepsHelper";
  import {
    AUTO_DELAY_MS,
    autoplayAllowed,
    watchSeen,
  } from "../../highlights/book-autoplay";
  import { urlsOf } from "../../slideshow/assets";
  import { makeFilm, slideIndexOf, spokenCaption } from "../../slideshow/film";
  import { SlideshowPlayer } from "../../slideshow/player";
  import type { PageContent, Playlist } from "../../slideshow/playlist";
  import { MIN_PHOTOS, playableMedia, sprintf } from "../../slideshow/playlist";
  import { arrivalSource } from "../../slideshow/share";
  import { mediaOf } from "../../slideshow/timeline";
  import { track } from "../../slideshow/track";
  import SlideshowControls from "./SlideshowControls.vue";
  import SlideshowIcon from "./SlideshowIcon.vue";

  declare const lavendlaObituary: {
    common: Record<string, string>;
    currentEvents: Record<string, string>;
  };

  const store = useStore();
  const texts = slideshowTexts();
  const data = slideshowData();
  const name = data?.deceased.name ?? "";
  const years = data?.deceased.years ?? "";
  const inMemoryOf = lavendlaObituary.common?.inMemoryOf ?? "";
  const playable = hasSlideshow();
  const addPhotoText = playable
    ? (lavendlaObituary.currentEvents?.slideshowCta ?? "")
    : (texts.addMedia ?? "");
  const askForMedia = sprintf(
    texts.askForMedia ?? "",
    data?.deceased.callingName ?? ""
  );

  const stage = ref<HTMLElement | null>(null);
  const canvas = ref<HTMLCanvasElement | null>(null);
  const content = shallowRef<PageContent | null>(quickContent());
  const playlist = shallowRef<Playlist | null>(null);
  const started = ref(false);
  const playing = ref(false);
  const time = ref(0);
  const duration = ref(0);
  const caption = ref("");
  const currentId = ref<string | null>(null);
  // How far into the current photo the film is (the bar under its thumbnail).
  const slideProgress = ref(0);
  const stripEl = ref<HTMLElement | null>(null);
  const peeking = ref(false);
  const phone = useMediaQuery(PHONE_QUERY);
  /** A single click waits this long to see whether it's a double click (open the viewer). */
  const DOUBLE_CLICK_MS = 250;
  const reducedMotion = usePrefersReducedMotion();
  let player: SlideshowPlayer | null = null;
  let observer: IntersectionObserver | null = null;

  /**
   * Playing by itself (PRO-4397): seen for 2 s (the pill fills meanwhile), the film plays once,
   * silently, then the poster comes back. Pause freezes it and brings back the pill, which
   * carries on. Off screen it waits; the first touch elsewhere stops it; never with reduced motion.
   * `off`: stopped for good, or the visitor took over.
   */
  type Auto = "idle" | "counting" | "playing" | "paused" | "done" | "off";
  const auto = ref<Auto>("idle");
  /** Back in view while playing: it carries on after … */
  const AUTO_RESUME_MS = 800;
  let autoTimer = 0;
  let unwatchSeen: (() => void) | null = null;
  const autoActive = () => auto.value === "playing" || auto.value === "paused";
  let peekTimer = 0;

  function film(c: PageContent) {
    const f = makeFilm(c, "landscape", reducedMotion.value);
    playlist.value = f.playlist;
    return f.timeline;
  }

  if (content.value && playable) {
    film(content.value);
  }

  const pagePhotos = computed(() =>
    content.value ? playableMedia(content.value) : []
  );
  /** Ideas for what to add, one per place; the page's photos take the first ones. */
  const places = [
    { hint: texts.placeEarlyYearsHint, title: texts.placeEarlyYears },
    { hint: texts.placeFamilyHint, title: texts.placeFamily },
    { hint: texts.placeMomentsHint, title: texts.placeMoments },
  ];
  const emptyPlaces = computed(() =>
    places.slice(pagePhotos.value.length, MIN_PHOTOS)
  );

  /** A real photo as the poster: the first photo of the slideshow. */
  const poster = computed(() => firstFrame(playlist.value));

  const strip = computed(() => {
    let n = 0;
    return (playlist.value?.slides ?? []).flatMap((s) => {
      const media = mediaOf(s);
      if (!media) {
        return [];
      }
      n += 1;
      return [
        {
          film: media.kind === "clip",
          id: media.id,
          number: n,
          thumb: media.thumb,
        },
      ];
    });
  });

  function ensurePlayer() {
    if (player || !canvas.value || !content.value) {
      return player;
    }
    player = new SlideshowPlayer(canvas.value, film(content.value), {
      assets,
      captions: true,
      onEnd: () => {
        playing.value = false;
        // Played through by itself: back to the poster.
        if (autoActive()) {
          auto.value = "done";
          started.value = false;
          player?.seek(0);
        }
      },
      onFrame: (t, d) => {
        time.value = t;
        duration.value = d;
        const seg = player?.segment;
        slideProgress.value = seg
          ? Math.min(1, Math.max(0, (t - seg.start) / (seg.td + seg.hold)))
          : 0;
      },
      onSlide: (_i, seg) => {
        currentId.value = mediaOf(seg.slide)?.id ?? null;
        caption.value = spokenCaption(seg.slide);
      },
      reducedMotion: reducedMotion.value,
    });
    player.captionLift = captionLift.target();
    // Once faces and sizes are known, lay the film out again from where it is.
    loadPageContent().then((full) => {
      if (full && player) {
        content.value = full;
        player.relayout(film(full));
      }
    });
    return player;
  }

  function play(fromId: string | null = null) {
    stopAutoTimer();
    auto.value = "off";
    // Phones get the full-screen 9:16 player instead of a small inline one.
    if (phone.value) {
      openViewer({ source: fromId ? "photo" : "section", startId: fromId });
      return;
    }
    const p = ensurePlayer();
    if (!p) {
      return;
    }
    if (fromId && playlist.value) {
      p.showPhoto(slideIndexOf(playlist.value, fromId));
    }
    const focusInPoster = posterHasFocus();
    started.value = true;
    p.play();
    playing.value = true;
    if (focusInPoster) {
      focusControls();
    }
    track({
      action: "play",
      source: fromId ? "photo_inline" : "section_inline",
    });
  }

  function toggle() {
    if (!player) {
      return;
    }
    // Playing by itself, the controls pause it and carry it on in the same way.
    if (auto.value === "playing") {
      pauseAuto();
      return;
    }
    if (auto.value === "paused") {
      resumeAuto();
      return;
    }
    if (player.playing) {
      player.pause();
    } else {
      player.play();
    }
    playing.value = player.playing;
  }

  function seek(t: number) {
    // Moving the bar takes over from playing by itself.
    if (autoActive()) {
      stopAutoTimer();
      auto.value = "off";
    }
    player?.seek(t);
  }

  /** Whether keyboard focus is on the poster, which goes away when the film starts. */
  function posterHasFocus(): boolean {
    return !!stage.value
      ?.querySelector(".slideshow-section__poster")
      ?.contains(document.activeElement);
  }

  /** The poster is gone: focus moves to the controls' play/pause, not to the page. */
  function focusControls() {
    nextTick(() => {
      stage.value
        ?.querySelector<HTMLElement>(".slideshow-controls__button")
        ?.focus({ preventScroll: true });
    });
  }

  function stopAutoTimer() {
    window.clearTimeout(autoTimer);
    autoTimer = 0;
  }

  /** The film from its start, silently (the inline player has no music). */
  function startAuto() {
    const p = ensurePlayer();
    if (!p) {
      auto.value = "off";
      return;
    }
    p.seek(0);
    started.value = true;
    auto.value = "playing";
    p.play();
    playing.value = true;
  }

  function pauseAuto() {
    stopAutoTimer();
    player?.pause();
    playing.value = false;
    auto.value = "paused";
  }

  /** The pill after a pause: on phones the visitor gets the full player with sound. */
  function resumeAuto() {
    if (phone.value) {
      const t = player?.time ?? 0;
      auto.value = "off";
      openViewer({ source: "section", startTime: t });
      return;
    }
    auto.value = "playing";
    player?.play();
    playing.value = true;
  }

  /** The first click or tap stops it: waiting ends for good, playing pauses. */
  function onTouch(e: PointerEvent) {
    // A mouse click plays or pauses through onStageClick instead.
    if (e.pointerType === "mouse") {
      return;
    }
    if (
      (e.target as Element).closest(
        ".slideshow-controls, .slideshow-section__play"
      )
    ) {
      return;
    }
    if (auto.value === "idle" || auto.value === "counting") {
      stopAutoTimer();
      auto.value = "off";
    } else if (auto.value === "playing") {
      pauseAuto();
    }
  }

  function onSeen(seen: boolean) {
    if (seen && auto.value === "idle") {
      auto.value = "counting";
      autoTimer = window.setTimeout(() => {
        // A keyboard user on the poster's play button keeps it: the film waits for them.
        if (posterHasFocus()) {
          auto.value = "off";
        } else if (auto.value === "counting") {
          startAuto();
        }
      }, AUTO_DELAY_MS);
    } else if (!seen && auto.value === "counting") {
      stopAutoTimer();
      auto.value = "idle";
    } else if (!seen && auto.value === "playing") {
      // A resume still waiting must not play it off screen.
      stopAutoTimer();
      player?.pause();
    } else if (seen && auto.value === "playing" && !player?.playing) {
      autoTimer = window.setTimeout(() => {
        if (auto.value === "playing") {
          player?.play();
        }
      }, AUTO_RESUME_MS);
    }
  }

  function expand() {
    stopAutoTimer();
    auto.value = "off";
    const t = player?.time ?? 0;
    player?.pause();
    playing.value = false;
    openViewer({ source: "expand", startTime: t });
  }

  // The controls show on hover, after a tap and while paused; captions step up above them.
  const captionLift = useCaptionLift(
    () => player,
    computed(() => peeking.value || !playing.value)
  );

  /** The mouse shows the controls while it moves; they hide again after a while of stillness. */
  const onEnter = () => {
    peek();
  };
  const onLeave = () => {
    clearTimeout(peekTimer);
    peeking.value = false;
  };

  let clickTimer = 0;

  /**
   * A mouse click on the playing film plays or pauses it, as in the viewer; a double click
   * opens it large instead, so the single click waits a moment to see which it is. A tap
   * on a touch screen only shows the controls.
   */
  function onStageClick(e: MouseEvent) {
    // Playing by itself too: toggle() pauses it and carries it on.
    if (
      !started.value ||
      (e.target as HTMLElement).closest(".slideshow-controls, button, a")
    ) {
      return;
    }
    if (
      (e as PointerEvent).pointerType &&
      (e as PointerEvent).pointerType !== "mouse"
    ) {
      return;
    }
    clearTimeout(clickTimer);
    clickTimer = window.setTimeout(toggle, DOUBLE_CLICK_MS);
  }

  function onDoubleClick(e: MouseEvent) {
    clearTimeout(clickTimer);
    if (
      !started.value ||
      (e.target as HTMLElement).closest(".slideshow-controls")
    ) {
      return;
    }
    expand();
  }

  function peek() {
    peeking.value = true;
    clearTimeout(peekTimer);
    peekTimer = window.setTimeout(() => {
      peeking.value = false;
    }, CONTROLS_IDLE_MS);
  }

  function addPhoto() {
    store.dispatch("createComment/toggleModal", MEDIA_INITIAL);
  }

  onMounted(() => {
    if (!(playable && stage.value)) {
      return;
    }
    // Look up faces and load the first photos when the section comes near, not on every page view.
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer?.disconnect();
          loadPageContent();
          assets.preload(urlsOf((playlist.value?.slides ?? []).slice(0, 3)), 3);
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(stage.value);
    // A double click on the playing film opens it large, in the viewer.
    stage.value.addEventListener("dblclick", onDoubleClick);
    stage.value.addEventListener("click", onStageClick);
    stage.value.addEventListener("mouseenter", onEnter);
    stage.value.addEventListener("mousemove", onEnter);
    stage.value.addEventListener("mouseleave", onLeave);
    if (autoplayAllowed()) {
      unwatchSeen = watchSeen(stage.value, onSeen);
    } else {
      auto.value = "off";
    }

    // A shared link (…#bildspel) opens the page with the slideshow playing, in the viewer;
    // the QR code on a computer (…#bildspel-dela) opens it on the phone straight into sharing.
    if (data && window.location.hash === `#${data.sectionId}-dela`) {
      openViewer({ share: true, source: arrivalSource() ?? "qr_handoff" });
    } else if (data && window.location.hash === `#${data.sectionId}`) {
      openViewer({ source: arrivalSource() ?? "link" });
    }
  });

  // The strip follows the film: the current photo's thumbnail scrolls to the middle (the
  // strip only, never the page).
  watch(currentId, async () => {
    await nextTick();
    const row = stripEl.value;
    const thumb = row?.querySelector<HTMLElement>(".is-current");
    if (!(row && thumb && started.value)) {
      return;
    }
    row.scrollTo({
      behavior: reducedMotion.value ? "auto" : "smooth",
      left: thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2,
    });
  });

  onBeforeUnmount(() => {
    stage.value?.removeEventListener("dblclick", onDoubleClick);
    stage.value?.removeEventListener("click", onStageClick);
    stage.value?.removeEventListener("mouseenter", onEnter);
    stage.value?.removeEventListener("mousemove", onEnter);
    stage.value?.removeEventListener("mouseleave", onLeave);
    clearTimeout(clickTimer);
    observer?.disconnect();
    unwatchSeen?.();
    stopAutoTimer();
    player?.destroy();
    clearTimeout(peekTimer);
  });
</script>
